import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { SiteFooter } from "@/components/site-footer";
import { PerfumeCatalogue } from "@/components/perfume-catalogue";
import { allPerfumesQueryOptions } from "@/lib/shopify-queries";
import { getCurrentChapter, isPurchasable } from "@/config/chapters";
import { STATE_COPY } from "@/config/chapterCopy";



export const Route = createFileRoute("/products/")({
  head: () => ({
    meta: [
      { title: "All Perfumes — House of Apocrypha" },
      {
        name: "description",
        content:
          "The full catalogue of House of Apocrypha — natural perfumes composed by hand in small seasonal batches.",
      },
      { property: "og:title", content: "All Perfumes — House of Apocrypha" },
      {
        property: "og:description",
        content:
          "Browse every natural perfume from House of Apocrypha, each released in a single numbered run.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://houseofapocrypha.com/products" },
    ],
    links: [{ rel: "canonical", href: "https://houseofapocrypha.com/products" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "All Perfumes — House of Apocrypha",
          description:
            "The full catalogue of House of Apocrypha — natural perfumes composed by hand in small seasonal batches.",
          url: "https://houseofapocrypha.com/products",
        }),
      },
    ],
  }),
  loader: ({ context }) =>
    context.queryClient.ensureQueryData(allPerfumesQueryOptions),
  pendingComponent: ProductsIndexPending,
  pendingMs: 200,
  pendingMinMs: 400,
  errorComponent: ProductsIndexError,
  component: ProductsIndex,
});

function ProductsIndexPending() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-7xl px-6 pt-16 pb-28">
        <div className="h-3 w-24 animate-pulse bg-muted/40" />
        <div className="mt-6 h-10 w-72 animate-pulse bg-muted/40" />
        <div className="mt-5 h-4 w-2/3 animate-pulse bg-muted/30" />
        <div className="mt-2 h-4 w-1/2 animate-pulse bg-muted/30" />
        <ul className="mt-16 grid gap-x-8 gap-y-14 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <li key={i} className="flex flex-col">
              <div className="aspect-[3/4] animate-pulse border border-border bg-muted/20" />
              <div className="mt-4 h-4 w-3/4 animate-pulse bg-muted/30" />
              <div className="mt-2 h-3 w-1/2 animate-pulse bg-muted/20" />
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function ProductsIndexError() {
  return (
    <div className="min-h-screen bg-background">
      <PageHeader />
      <main className="mx-auto max-w-7xl px-6 py-32 text-center">
        <p className="eyebrow text-muted-foreground">The Catalogue</p>
        <h1 className="mt-4 font-display text-3xl text-parchment">
          The shelf is loading slowly tonight
        </h1>
        <p className="mx-auto mt-4 max-w-md text-sm text-muted-foreground">
          We couldn&apos;t reach the apothecary just now. Please refresh in a moment.
        </p>
      </main>
      <SiteFooter />
    </div>
  );
}

function ProductsIndex() {
  const chapter = getCurrentChapter();
  const stateCopy = STATE_COPY[chapter.state];
  return (

    <div className="min-h-screen bg-background">
      <PageHeader />
      <main>
        <nav className="mx-auto max-w-7xl px-6 pt-8 text-sm">
          <span className="text-parchment">The Current Chapter</span>
        </nav>

        <header className="mx-auto max-w-7xl px-6 pt-10 pb-2">
          <p className="eyebrow">{stateCopy.statusLabel}</p>
          <h1 className="mt-4 text-4xl text-parchment sm:text-5xl">
            The <span className="italic text-gold/90">Current Chapter</span>
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-relaxed text-muted-foreground">
            Each fragrance belongs to a seasonal chapter. Bottles are offered by
            reserve for a brief window. Once the chapter closes, the work
            continues: resting, filtering, bottling, and dispatch.
          </p>
          {isPurchasable(chapter.state) ? (
            <div className="mt-6 max-w-2xl border border-gold/40 bg-gold/5 px-5 py-4">
              <p className="font-display text-xs uppercase tracking-[0.22em] text-gold">
                Reserve open while the chapter is in its window
              </p>
              <p className="mt-2 font-serif text-sm leading-relaxed text-parchment/90">
                Next shipment dispatches {chapter.dispatch.toLowerCase()}.
              </p>
            </div>
          ) : (
            <div className="mt-6 max-w-2xl border border-gold/40 bg-gold/5 px-5 py-4">
              <p className="font-display text-xs uppercase tracking-[0.22em] text-gold">
                {stateCopy.statusLabel}
              </p>
              <p className="mt-2 font-serif text-sm leading-relaxed text-parchment/90">
                {stateCopy.productNote}
              </p>
            </div>
          )}
        </header>



        <PerfumeCatalogue />

        <section className="mx-auto max-w-3xl px-6 py-20 text-center sm:py-28">
          <div className="ornament">
            <span>✶</span>
          </div>
          <p className="mt-10 text-lg leading-relaxed text-muted-foreground">
            {chapter.vialsAvailable
              ? "Chapter Vials are available while the chapter is open. Every bottle arrives with a companion vial of the same fragrance."
              : "Chapter Vials return with the next chapter. Every bottle arrives with a companion vial of the same fragrance."}
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-3 sm:flex-row sm:gap-4">
            <Link
              to="/how-it-works"
              className="group inline-flex items-center justify-center gap-3 border border-gold/60 bg-background/20 px-9 py-4 font-display text-xs uppercase tracking-[0.26em] text-parchment transition-colors duration-500 hover:border-gold hover:bg-gold/10"
            >
              The Vial Promise
              <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
            </Link>
            {chapter.vialsAvailable && (
              <Link
                to="/products/$slug"
                params={{ slug: "chapter-vials" }}
                className="group inline-flex items-center justify-center gap-3 border border-gold bg-gold px-9 py-4 font-display text-xs uppercase tracking-[0.26em] text-ink transition-all duration-300 ease-out hover:bg-gold-soft hover:shadow-[0_0_20px_rgba(184,164,123,0.25)] active:scale-[0.97]"
              >
                Shop Chapter Vials
                <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
              </Link>
            )}
          </div>

        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
